import React, { useRef, useState } from 'react';
import { Button, Tag } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import List, { ListRef } from '../../components/Templates/List';
import { UpsertModal, type FormFieldConfig } from '../../components/Templates/UpsertModal';
import type { FieldConfig, ForgeQuery } from '../../components/ForgeQueryBuilder';
import { preRatingThresholdApi } from '../../services/manager/pre-rating-threshold';
import type { PreRatingThreshold } from '../../types/entity/pre-rating-threshold';
import { PreRatingThresholdLabels } from '../../types/entity/pre-rating-threshold';

const packageTypeOptions = [
  { label: '随租随还', value: 'return_anytime' },
  { label: '租完可归还', value: 'return_at_end' },
];

const dominantModeOptions = [
  { label: '提前归还', value: 'EARLY_RETURN' },
  { label: '提前买断', value: 'EARLY_BUYOUT' },
  { label: '到期归还', value: 'END_RETURN' },
  { label: '到期买断', value: 'END_BUYOUT' },
];

const PreRatingThresholds: React.FC = () => {
  const listRef = useRef<ListRef>(null);
  const [modalState, setModalState] = useState<{
    open: boolean;
    record: PreRatingThreshold | null;
  }>({
    open: false,
    record: null,
  });

  const queryFields: FieldConfig[] = [
    { key: 'packageType', label: PreRatingThresholdLabels.packageType, type: 'select', options: packageTypeOptions },
    { key: 'dominantMode', label: PreRatingThresholdLabels.dominantMode, type: 'select', options: dominantModeOptions },
  ];

  const formFields: FormFieldConfig[] = [
    {
      name: 'packageType',
      label: PreRatingThresholdLabels.packageType,
      type: 'text',
      disabled: true,
    },
    {
      name: 'dominantMode',
      label: PreRatingThresholdLabels.dominantMode,
      type: 'text',
      disabled: true,
    },
    {
      name: 'dominantModeRatio',
      label: PreRatingThresholdLabels.dominantModeRatio,
      type: 'text',
      disabled: true,
    },
    {
      name: 'sampleOrderCount',
      label: PreRatingThresholdLabels.sampleOrderCount,
      type: 'text',
      disabled: true,
    },
    {
      name: 'thresholdProfitRate',
      label: PreRatingThresholdLabels.thresholdProfitRateRaw,
      type: 'number',
      required: true,
      fieldProps: {
        min: 0,
        precision: 4,
        step: 0.0001,
      },
    },
  ];

  const columns: ColumnsType<PreRatingThreshold> = [
    { title: 'ID', dataIndex: 'id', width: 90, fixed: 'left' },
    {
      title: PreRatingThresholdLabels.packageType,
      dataIndex: 'packageType',
      width: 140,
      render: (value?: string) => renderPackageType(value),
    },
    {
      title: PreRatingThresholdLabels.dominantMode,
      dataIndex: 'dominantMode',
      width: 140,
      render: (value?: string) => renderDominantMode(value),
    },
    {
      title: PreRatingThresholdLabels.dominantModeRatio,
      dataIndex: 'dominantModeRatio',
      width: 140,
      render: (value?: number) => renderRate(value),
    },
    {
      title: PreRatingThresholdLabels.thresholdProfitRate,
      dataIndex: 'thresholdProfitRate',
      width: 140,
      render: (value?: number) => renderRate(value),
    },
    {
      title: PreRatingThresholdLabels.sampleOrderCount,
      dataIndex: 'sampleOrderCount',
      width: 120,
    },
    {
      title: PreRatingThresholdLabels.statsStartedAt,
      dataIndex: 'statsStartedAt',
      width: 180,
      render: (value?: string) => formatDateTime(value),
    },
    {
      title: PreRatingThresholdLabels.statsEndedAt,
      dataIndex: 'statsEndedAt',
      width: 180,
      render: (value?: string) => formatDateTime(value),
    },
    {
      title: PreRatingThresholdLabels.generatedAt,
      dataIndex: 'generatedAt',
      width: 180,
      render: (value?: string) => formatDateTime(value),
    },
    {
      title: '更新时间',
      width: 180,
      render: (_, record) => formatDateTime(record.updatedAt || record.updated_at),
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          size="small"
          onClick={() => setModalState({ open: true, record })}
        >
          编辑
        </Button>
      ),
    },
  ];

  return (
    <>
      <List<PreRatingThreshold>
        ref={listRef}
        title="预评级利润阈值配置"
        queryFields={queryFields}
        fetcher={async (params) => {
          const response = await preRatingThresholdApi.list(params as any);
          return {
            rows: response.data.items || [],
            pagination: response.data.pagination,
          };
        }}
        buildParams={(q: ForgeQuery) => ({
          page: q.page || 1,
          pageSize: q.pageSize || 20,
          filter: q.filter || {},
          sort: q.sort || [{ field: 'id', order: 'asc' }],
        })}
        columns={columns}
        rowKey={(row) => String(row.id)}
        tableProps={{ scroll: { x: 1500 } }}
      />

      <UpsertModal<PreRatingThreshold>
        title="编辑预评级利润阈值"
        open={modalState.open}
        mode="edit"
        record={modalState.record}
        onCancel={() => setModalState({ open: false, record: null })}
        onSuccess={() => {
          setModalState({ open: false, record: null });
          listRef.current?.reload();
        }}
        updateApi={(id, data) => preRatingThresholdApi.updateById(id, data)}
        formFields={formFields}
        width={640}
        beforeEdit={(record) => ({
          ...record,
          packageType: packageTypeText(record.packageType),
          dominantMode: dominantModeText(record.dominantMode),
          dominantModeRatio: renderRate(record.dominantModeRatio),
          sampleOrderCount: record.sampleOrderCount ?? 0,
          thresholdProfitRate: Number(record.thresholdProfitRate ?? 0),
        })}
        beforeSubmit={(values) => ({
          thresholdProfitRate: Number(values.thresholdProfitRate ?? 0),
        })}
      />
    </>
  );
};

function renderPackageType(value?: string) {
  return <Tag color="blue">{packageTypeText(value)}</Tag>;
}

function renderDominantMode(value?: string) {
  return <Tag color="geekblue">{dominantModeText(value)}</Tag>;
}

function renderRate(value?: number) {
  return `${(Number(value || 0) * 100).toFixed(2)}%`;
}

function packageTypeText(value?: string) {
  switch (value) {
    case 'return_anytime':
      return '随租随还';
    case 'return_at_end':
      return '租完可归还';
    default:
      return value || '-';
  }
}

function dominantModeText(value?: string) {
  switch (value) {
    case 'EARLY_RETURN':
      return '提前归还';
    case 'EARLY_BUYOUT':
      return '提前买断';
    case 'END_RETURN':
      return '到期归还';
    case 'END_BUYOUT':
      return '到期买断';
    default:
      return value || '-';
  }
}

function formatDateTime(value?: string) {
  if (!value) {
    return '-';
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : value;
}

export default PreRatingThresholds;
